import styled from 'styled-components';
import { ReactComponent as DownAngle } from '../../../assets/img/icons/DownAngle.svg';
import { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import store, { RootState } from 'store';
import { CONTACT_ANALYTICS } from 'constants/types';
import { getConstantCampaignEmailId } from '../utils';
import { getBouncedContactsList, setBounceFilter, setBounceFilterFirstTime } from './action';
import useVisible from '../Shared/useVisible';

const DEFAULT_PAGE_LIMIT = 20;
const bounceListLabel = [
  { label: 'All', value: '' },
  { label: 'Soft Bounced', value: 'soft_bounce' },
  { label: 'Hard Bounced', value: 'hard_bounce' },
];

interface IBounceTypeFilterComponent {
  tab?: string;
  searchedText: string;
  accountId: number;
}

const BounceTypeFilterComponent: FC<IBounceTypeFilterComponent> = (props: IBounceTypeFilterComponent) => {
  const { searchedText, accountId } = props;
  const { ref, isVisible, setIsVisible } = useVisible(false);
  const AllCampaignsFilterCheck = useSelector((state: RootState) => state.analyticsDashboard.appliedCampaignsFilters.isAllCampaigns);
  const bounceFilter = useSelector((state: RootState) => state.contactAnalytics.bounceFilter);
  const isBounceFilterAppliedFirstTime = useSelector((state: RootState) => state.contactAnalytics.isBounceFilterAppliedFirstTime);
  const [bounceType, setBounceType] = useState(bounceFilter);
  const [filterLabel, setFilterLabel] = useState('');
  const getFilteredResults = () => {
    setIsVisible(false);
    store.dispatch({
      type: CONTACT_ANALYTICS.BOUNCED_RESET,
    });
    let analyticsDashboard = store.getState().analyticsDashboard;
    let analyticsGraph = store.getState().analyticsGraph;
    let { appliedCampaignIds, appliedAssetsIds, excludeCampaignsIds, excludeAssetsIds } = analyticsDashboard;
    let { isShareProfile, isDailyIQ, isWeeklyAccountInsights } = analyticsDashboard.appliedCampaignsFilters;
    let { startDate, endDate } = analyticsGraph;
    let _params: any = {
      limit: DEFAULT_PAGE_LIMIT,
      tab: 'bounced',
      searchWord: searchedText,
      offset: 0,
      emailIds: appliedAssetsIds,
      startDate: startDate,
      endDate: endDate,
      allCampaigns: AllCampaignsFilterCheck,
      campaignIds: appliedCampaignIds,
    };
    if (isShareProfile || isDailyIQ || isWeeklyAccountInsights) {
      let email_campaign_id = getConstantCampaignEmailId({ isShareProfile, isDailyIQ, isWeeklyAccountInsights });
      _params['emailIds'] = [email_campaign_id];
      _params['campaignIds'] = [email_campaign_id];
    }

    if (AllCampaignsFilterCheck) {
      _params['excludeCampaignsIds'] = excludeCampaignsIds;
      _params['excludeAssetsIds'] = excludeAssetsIds;
    }

    if (bounceType) {
      _params['bounce_filter'] = bounceType;
    }
    if (accountId) _params['accountId'] = accountId;
    getBouncedContactsList(_params, accountId >= 0);
  };

  useEffect(() => {
    setFilterLabel(bounceListLabel.filter((i: any) => i.value === bounceFilter)[0]?.label);
  });

  useEffect(() => {
    if (bounceFilter !== bounceType) {
      setBounceFilter(bounceType);
      getFilteredResults();
    }
  }, [bounceType]);

  return (
    <Wrapper>
      <div
        className={`dropdown-selecter`}
        onClick={() => {
          setIsVisible(!isVisible);
        }}
        onBlur={() => setIsVisible(false)}
      >
        <div className="label">
          <span className="label-eclips">{isBounceFilterAppliedFirstTime ? filterLabel : 'Bounce Type'}</span>
        </div>

        <div className="d-flex">
          <div className="seprator"></div>
          <div className="p-2">
            <DownAngle />
          </div>
        </div>
      </div>
      <div ref={ref} className={`dropdown-options ${isVisible ? 'show' : ''}`}>
        {/* Toggle class "show" to show and hide dropdown */}
        <div className="dropdown-options-inner">
          <div className={`dropdown-top-filters`}>
            <ul>
              {bounceListLabel.map((item: any) => {
                return (
                  <li
                    onClick={() => {
                      setBounceType(item.value);
                      setFilterLabel(item.label);
                      if (!isBounceFilterAppliedFirstTime) setBounceFilterFirstTime(true);
                    }}
                  >
                    <label className={`${bounceType == item.value ? 'label-style' : ''}`}>{item.label}</label>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default BounceTypeFilterComponent;

const Wrapper = styled.div`
  position: relative;
  .disabled {
    pointer-events: none;
    opacity: 0.6;
  }
  .dropdown-selecter {
    border: 1px solid #dadada;
    min-width: 160px;
    background: #fff;
    border-radius: 8px;
    min-height: 38px;
    cursor: pointer;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    padding-left: 10px;
    padding-right: 2px;
    height: 38px;
    float: right;
    width: auto;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      min-width: 120px;
      border-radius: 6px;
      height: 30px;
      min-height: 30px;
    }
    &:focus,
    &:active,
    &.active {
      box-shadow: 0 0 0 0.1rem rgb(40 167 69 / 50%);
    }
    .label {
      color: #7e7e7e;
      font-size: 16px;
      font-weight: 500;
      font-family: 'Roboto', sans-serif;
      flex-wrap: wrap;
      padding: 2px 0px 2px 4px;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      width: calc(100% - 30px);
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
        margin-top: 3px;
      }
      .label-eclips {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        display: block;
        float: left;
        min-width: 80px;
        max-width: 200px;
      }
    }
    .seprator {
      -webkit-align-self: stretch;
      -ms-flex-item-align: stretch;
      align-self: stretch;
      background-color: #dddddd;
      margin-bottom: 8px;
      margin-top: 8px;
      width: 1px;
      box-sizing: border-box;
      + .p-2 {
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          padding-top: 3px !important;
          padding-bottom: 3px !important;
        }
      }
    }
  }
  .label-style {
    color: #1ec252 !important;
  }
  .dropdown-options {
    background: #fff;
    position: absolute;
    top: 35px;
    right: -40px;
    z-index: 9;
    min-width: 60%;
    width: auto;
    border-radius: 8px;
    width: 138px;
    transition-duration: 0.5s;
    overflow: hidden;
    height: 0px;
    opacity: 0;
    box-shadow: 0px 5px 14px 0px rgb(0 0 0 / 28%);
    &.show {
      height: auto;
      opacity: 1;
    }
    .dropdown-options-inner {
      border: 1px solid #def6e6;
      box-shadow: 0 0 40px 0 rgb(0 0 0 / 28%);
      background: #fff;
      border-radius: 8px;
      float: left;
      .dropdown-top-filters {
        padding: 8px 8px 8px 8px;
        float: left;
        width: 100%;
        ul {
          width: 100%;
          float: left;
          display: block;
          padding: 0 0 0 10px;
          margin: 0;
          li {
            list-style-type: none;
            margin: 5px 0;
            width: 100%;
            display: block;
            float: left;
            label {
              font-family: 'Roboto', sans-serif;
              color: #7e7e7e;
              font-size: 16px;
              font-weight: 500;
              cursor: pointer;
              display: block;
              padding: 0px 0;
              float: left;
              &:hover {
                color: #27ce0a;
              }
              .ant-checkbox-wrapper {
                display: inline-block;
                float: left;
                &::after {
                  display: none;
                }
              }
              > div {
                float: left;
                margin: 8px 12px;
              }
            }
          }
        }
      }
    }
  }
`;
